"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, type RefObject } from "react";

// One spring owns wheel motion from the first event through the final alignment.
export function stepScroll(position: number, velocity: number, target: number, seconds: number) {
  const frequency = 13,
    offset = position - target,
    momentum = velocity + frequency * offset,
    decay = Math.exp(-frequency * seconds);
  return {
    position: target + (offset + momentum * seconds) * decay,
    velocity: (velocity - frequency * momentum * seconds) * decay
  };
}
export function useSectionScroll(page) {
  const $ = _c(4);
  let t0;
  if ($[0] !== page.current) {
    t0 = () => {
      if (!page.current) {
        return;
      }
      const root = page.current;
      const html = document.documentElement;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
      let stops = [];
      let targetKey = "";
      let frame = 0;
      let position = window.scrollY;
      let target = position;
      let velocity = 0;
      let lastFrame = 0;
      let lastWheel = -Infinity;
      let direction = 0;
      let reverseDistance = 0;
      let nestedGesture = false;
      let written = position;
      let focusAfter = null;
      const maximum = () => Math.max(0, html.scrollHeight - window.innerHeight);
      const clamp = value => Math.max(0, Math.min(maximum(), value));
      const cancel = function cancel() {
        cancelAnimationFrame(frame);
        frame = 0;
        velocity = 0;
        position = target = written = window.scrollY;
        focusAfter = null;
      };
      const measure = function measure() {
        const height = Math.max(160, window.innerHeight - 64);
        const y = window.scrollY;
        const points = [{
          key: "start",
          position: 0
        }, {
          key: "end",
          position: maximum()
        }];
        root.querySelectorAll("[data-section]").forEach((section, index) => {
          const top = Math.max(0, section.getBoundingClientRect().top + y - 64);
          const end = top + section.offsetHeight - height;
          points.push({
            key: `${index}:top`,
            position: clamp(top)
          });
          let last = top;
          let step = 0;
          for (let next = top + height; next < end; next = next + height, next) {
            points.push({
              key: `${index}:${step++}`,
              position: clamp(next)
            });
            last = next;
          }
          if (end - last > 96) {
            points.push({
              key: `${index}:bottom`,
              position: clamp(end)
            });
          }
        });
        stops = [...new Map(points.map(_temp)).values()].sort(_temp2);
        target = stops.find(stop_0 => stop_0.key === targetKey)?.position ?? clamp(target);
      };
      function tick(now) {
        if (html.style.overflow === "hidden") {
          cancel();
          return;
        }
        const seconds = Math.max(0, (now - lastFrame) / 1000);
        lastFrame = now;
        const {
          position: t1,
          velocity: t2
        } = stepScroll(position, velocity, target, seconds);
        position = t1;
        velocity = t2;
        const done = Math.abs(position - target) < 0.35 && Math.abs(velocity) < 4;
        if (done) {
          position = target;
        }
        window.scrollTo({
          top: position,
          behavior: "instant"
        });
        written = window.scrollY;
        if (done) {
          frame = 0;
          velocity = 0;
          focusAfter?.focus({
            preventScroll: true
          });
          focusAfter = null;
        } else {
          frame = requestAnimationFrame(tick);
        }
      }
      const go = function go(value_0, focus) {
        html.dataset.atlasScroll = "controlled";
        if (!frame) {
          position = written = window.scrollY;
          velocity = 0;
          lastFrame = performance.now();
        }
        target = clamp(value_0);
        targetKey = stops.find(stop_1 => Math.abs(stop_1.position - target) < 1)?.key || "";
        focusAfter = focus || null;
        if (reduced.matches) {
          cancel();
          window.scrollTo({
            top: clamp(value_0),
            behavior: "instant"
          });
          focus?.focus({
            preventScroll: true
          });
          return;
        }
        if (!frame) {
          frame = requestAnimationFrame(tick);
        }
      };
      const adjacent = function adjacent(sign) {
        const y_0 = window.scrollY;
        return sign > 0 ? stops.find(stop_2 => stop_2.position > y_0 + 3)?.position ?? maximum() : [...stops].reverse().find(stop_3 => stop_3.position < y_0 - 3)?.position ?? 0;
      };
      const nested = function nested(element, delta) {
        let node = element instanceof Element ? element : null;
        while (node && node !== document.body && node !== html) {
          if (node.matches("[role=\"dialog\"], [data-native-scroll], input, textarea, select, [contenteditable=\"true\"]")) {
            return true;
          }
          const style = getComputedStyle(node);
          if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1 && (delta < 0 ? node.scrollTop > 0 : node.scrollTop + node.clientHeight < node.scrollHeight - 1)) {
            return true;
          }
          node = node.parentElement;
        }
        return false;
      };
      const wheel = function wheel(event) {
        if (reduced.matches || event.ctrlKey || event.metaKey || html.style.overflow === "hidden" || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
          return;
        }
        const delta_0 = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
        const now_0 = performance.now();
        const sign_0 = Math.sign(delta_0);
        const fresh = now_0 - lastWheel > 180;
        const inside = nested(event.target, delta_0);
        if (fresh) {
          nestedGesture = inside;
          reverseDistance = 0;
        } else {
          if (inside) {
            nestedGesture = true;
          }
        }
        lastWheel = now_0;
        if (nestedGesture) {
          cancel();
          if (!inside && event.cancelable) {
            event.preventDefault();
          }
          return;
        }
        if (!event.cancelable) {
          cancel();
          return;
        }
        event.preventDefault();
        html.dataset.atlasScroll = "controlled";
        reverseDistance = sign_0 !== direction ? reverseDistance + Math.abs(delta_0) : 0;
        if (fresh || reverseDistance >= 4) {
          direction = sign_0;
          reverseDistance = 0;
          go(adjacent(sign_0));
        }
      };
      const key = function key(event_0) {
        const node_0 = event_0.target instanceof Element ? event_0.target : null;
        if (event_0.altKey || event_0.ctrlKey || event_0.metaKey || html.style.overflow === "hidden" || node_0?.closest("button, a, input, textarea, select, [contenteditable=\"true\"], [role=\"tablist\"], [role=\"tree\"], [role=\"dialog\"]")) {
          return;
        }
        const sign_1 = event_0.key === "End" || event_0.key === "PageDown" || event_0.key === " " && !event_0.shiftKey ? 1 : event_0.key === "Home" || event_0.key === "PageUp" || event_0.key === " " && event_0.shiftKey ? -1 : 0;
        if (!sign_1 && event_0.key !== "Home" && event_0.key !== "End") {
          return;
        }
        if (nested(event_0.target, sign_1)) {
          return;
        }
        event_0.preventDefault();
        if (event_0.repeat && frame) {
          return;
        }
        go(event_0.key === "Home" ? 0 : event_0.key === "End" ? maximum() : adjacent(sign_1));
      };
      const anchor = function anchor(event_1) {
        if (event_1.button || event_1.ctrlKey || event_1.metaKey || event_1.shiftKey || event_1.altKey) {
          return;
        }
        const link = event_1.target instanceof Element ? event_1.target.closest("a[href^=\"#\"]") : null;
        const href = link?.getAttribute("href");
        if (!href || href.length < 2) {
          return;
        }
        const destination = document.getElementById(href.slice(1));
        if (!destination || !root.contains(destination)) {
          return;
        }
        event_1.preventDefault();
        if (window.location.hash !== href) {
          history.pushState(null, "", href);
        }
        if (!destination.hasAttribute("tabindex")) {
          destination.setAttribute("tabindex", "-1");
        }
        go(destination.getBoundingClientRect().top + window.scrollY - 64, destination);
      };
      const externalScroll = function externalScroll() {
        if (frame && Math.abs(window.scrollY - written) > 2) {
          cancel();
        }
      };
      const touch = function touch() {
        cancel();
        html.dataset.atlasScroll = "touch";
      };
      const visibility = function visibility() {
        if (document.hidden) {
          cancel();
        }
      };
      const preference = function preference() {
        cancel();
        html.dataset.atlasScroll = reduced.matches ? "reduced" : "controlled";
      };
      const focus_0 = function focus(event_2) {
        if ((event_2.target as Element)?.closest("[role=\"dialog\"]")) {
          cancel();
        }
      };
      measure();
      preference();
      const observer = new ResizeObserver(measure);
      observer.observe(root);
      window.addEventListener("wheel", wheel, {
        passive: false
      });
      window.addEventListener("keydown", key);
      document.addEventListener("click", anchor);
      window.addEventListener("scroll", externalScroll, {
        passive: true
      });
      window.addEventListener("resize", measure);
      window.addEventListener("pointerdown", cancel, {
        passive: true
      });
      window.addEventListener("touchstart", touch, {
        passive: true
      });
      window.addEventListener("popstate", cancel);
      document.addEventListener("visibilitychange", visibility);
      document.addEventListener("focusin", focus_0);
      reduced.addEventListener("change", preference);
      return () => {
        cancel();
        observer.disconnect();
        delete html.dataset.atlasScroll;
        window.removeEventListener("wheel", wheel);
        window.removeEventListener("keydown", key);
        document.removeEventListener("click", anchor);
        window.removeEventListener("scroll", externalScroll);
        window.removeEventListener("resize", measure);
        window.removeEventListener("pointerdown", cancel);
        window.removeEventListener("touchstart", touch);
        window.removeEventListener("popstate", cancel);
        document.removeEventListener("visibilitychange", visibility);
        document.removeEventListener("focusin", focus_0);
        reduced.removeEventListener("change", preference);
      };
    };
    $[0] = page.current;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== page) {
    t1 = [page];
    $[2] = page;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function _temp2(a, b) {
  return a.position - b.position;
}
function _temp(stop) {
  return [stop.position, stop];
}